import React, { useEffect, useRef, useState } from 'react';
import { Printer, X, Download, TrendingUp, IndianRupee, Calendar, User, Briefcase, Clock, Wallet, CheckCircle } from 'lucide-react';
import { siteConfigService } from '../../services/api';

const defaultCompany = {
  name: 'Ortus Food & Beverages',
  tagline: 'FMCG Platform',
  address: '123 Corporate Plaza, Business District, Mumbai, Maharashtra 400001',
  gst: '27AAAAA0000A1Z5',
  logo: null,
};

function InvestmentPrint({ investment, onClose }) {
  const printRef = useRef();
  const [downloading, setDownloading] = useState(false);
  const [company, setCompany] = useState(defaultCompany);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await siteConfigService.settings();
        const settings = response?.data || response || {};
        setCompany({
          name: settings.site_name || defaultCompany.name,
          tagline: settings.site_tagline || defaultCompany.tagline,
          address: settings.contact_address || defaultCompany.address,
          gst: settings.gst_number || defaultCompany.gst,
          logo: settings.site_logo || null,
        });
      } catch (error) {
        console.error('Failed to load company settings', error);
      }
    };

    loadSettings();
  }, []);

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Investment_${investment.investment_number}`;
    window.print();
    document.title = originalTitle;
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      handlePrint();
    } finally {
      setDownloading(false);
    }
  };

  if (!investment) return null;

  const calculateCurrentValuation = () => {
    const principal = parseFloat(investment.amount || 0);
    const rate = parseFloat(investment.interest_rate || 0) / 100;
    const startDate = new Date(investment.start_date);
    const today = new Date();
    
    if (today <= startDate) return principal;

    // Calculate difference in days
    const diffTime = Math.abs(today - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Simple Interest Formula: Principal * (1 + (Rate * (Days/365)))
    const earnedInterest = principal * (rate * (diffDays / 365));
    return principal + earnedInterest;
  };

  const currentValuation = calculateCurrentValuation();

  const getStatusStyle = (status) => {
    switch (status) {
      case 'active': return 'text-red-600 bg-red-50 border-red-200';
      case 'matured': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'withdrawn': return 'text-slate-600 bg-slate-50 border-slate-200';
      default: return 'text-amber-600 bg-amber-50 border-amber-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header Actions */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-900">Investment Certificate</h3>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              <Download size={18} />
              {downloading ? 'Preparing...' : 'Download PDF'}
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium shadow-lg shadow-red-200"
            >
              <Printer size={18} />
              Print
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-100" ref={printRef}>
          <style dangerouslySetInnerHTML={{ __html: `
            @media print {
              body * { visibility: hidden; }
              #printable-investment, #printable-investment * { visibility: visible; }
              #printable-investment { 
                position: absolute; 
                left: 0; 
                top: 0; 
                width: 100%;
                padding: 0 !important;
                margin: 0 !important;
              }
              .no-print { display: none !important; }
            }
          ` }} />
          
          <div id="printable-investment" className="max-w-2xl mx-auto bg-white shadow-sm p-10 rounded-sm border-t-8 border-red-600 min-h-[9in]">
            {/* Branding */}
            <div className="flex justify-between items-start mb-12">
              <div>
                {company.logo ? (
                  <img src={company.logo} alt="Company logo" className="h-12 object-contain mb-3" />
                ) : (
                  <h1 className="text-3xl font-black text-red-600 mb-1">{company.name.toUpperCase()}</h1>
                )}
                <p className="text-gray-500 text-[10px] uppercase tracking-[0.2em] font-bold">{company.tagline}</p>
                <div className="mt-6 text-xs text-gray-600 leading-relaxed max-w-[250px]">
                  <p className="font-bold text-gray-900">{company.name}</p>
                  <p>{company.address}</p>
                  <p className="mt-1">GSTIN: {company.gst || 'Not Provided'}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="inline-block px-4 py-1.5 rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-widest mb-4">
                  Official Record
                </div>
                <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-tight">Investment</h2>
                <h3 className="text-lg font-mono text-red-600 font-bold">{investment.investment_number}</h3>
                <div className="mt-4 space-y-1 text-xs text-gray-500">
                  <p>Issue Date: <span className="text-gray-900 font-bold">{new Date(investment.created_at).toLocaleDateString()}</span></p>
                  <p>Status: <span className={`font-bold uppercase ${investment.status === 'active' ? 'text-red-600' : 'text-amber-600'}`}>{investment.status}</span></p>
                </div>
              </div>
            </div>

            {/* Investor Details */}
            <div className="grid grid-cols-2 gap-12 mb-12 py-8 border-y border-gray-100">
              <div>
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Investor Information</h4>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-200">
                    <User size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-base leading-none mb-1">{investment.user?.name || 'Valued Investor'}</p>
                    <p className="text-xs text-gray-500">{investment.user?.email || '—'}</p>
                    <p className="text-[10px] text-red-600 font-mono mt-1">UID-{investment.user_id}</p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Asset Classification</h4>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-200">
                    <Briefcase size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-base leading-none mb-1 uppercase tracking-tight">{investment.type?.replace('_', ' ')}</p>
                    <p className="text-xs text-gray-500">Fixed Income Asset</p>
                    <p className="text-[10px] text-red-600 font-mono mt-1">Portfolio Verified</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="mb-12">
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6">Financial Deployment</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Principal Amount</p>
                  <div className="flex items-center gap-1 text-2xl font-black text-gray-900">
                    <span className="text-red-600">₹</span>
                    {parseFloat(investment.amount).toLocaleString()}
                  </div>
                </div>
                <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Annual Yield (ROI)</p>
                  <div className="flex items-center gap-1 text-2xl font-black text-gray-900">
                    {investment.interest_rate}%
                    <TrendingUp size={20} className="text-red-500" />
                  </div>
                </div>
                <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Current Valuation</p>
                  <div className="flex items-center gap-1 text-2xl font-black text-gray-900">
                    <span className="text-red-600">₹</span>
                    {currentValuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                {investment.maturity_amount && (
                  <div className="bg-blue-50/50 p-5 rounded-2xl border border-blue-100">
                    <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-2">Maturity Value</p>
                    <div className="flex items-center gap-1 text-2xl font-black text-blue-900">
                      <span className="text-blue-600">₹</span>
                      {parseFloat(investment.maturity_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="grid grid-cols-2 gap-8 mb-12">
              <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-100">
                <div className="text-red-500">
                  <Calendar size={20} />
                </div>
                <div>
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Start Date</p>
                  <p className="text-sm font-bold text-gray-900">{new Date(investment.start_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-100">
                <div className="text-red-500">
                  <Clock size={20} />
                </div>
                <div>
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Maturity Target</p>
                  <p className="text-sm font-bold text-gray-900">
                    {investment.maturity_date 
                      ? new Date(investment.maturity_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
                      : 'Open Ended'}
                  </p>
                </div>
              </div>
            </div>

            {/* Notes */}
            {investment.notes && (
              <div className="mb-12">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Additional Disclosures</h4>
                <div className="bg-red-50/50 p-4 rounded-xl text-xs text-gray-600 italic border border-red-100">
                  {investment.notes}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-auto pt-12 border-t border-gray-100">
              <div className="flex justify-between items-end">
                <div className="text-[10px] text-gray-400 leading-relaxed max-w-[400px]">
                  <p className="font-bold mb-1">Disclaimer & Notice</p>
                  <p>This document serves as an official confirmation of the capital deployment described herein. All investments are subject to the terms and conditions outlined in the master service agreement. This is a computer-generated record and does not require a physical signature.</p>
                </div>
                <div className="text-right">
                  <div className="h-16 w-32 bg-gray-50 rounded border border-dashed border-gray-200 flex items-center justify-center text-[10px] text-gray-300 uppercase font-black tracking-widest mb-2">
                    Digital Seal
                  </div>
                  <p className="text-[9px] font-bold text-gray-900">Authorized Signatory</p>
                  <p className="text-[8px] text-gray-400">{company.name}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvestmentPrint;
